import { appAlert } from '@/lib/appAlert';
import { Ionicons } from '@expo/vector-icons';
import { Link, useLocalSearchParams, useRouter } from 'expo-router';
import { useEffect, useMemo, useState } from 'react';
import {
  Image,
  ImageBackground,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { contentMaxWidth, fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/contexts/AuthContext';
import { setPendingInviteAgreement } from '@/lib/invite';
import { validateEmail, validatePassword } from '@/lib/validation';

const wallpaper = require('../../../assets/wallpaper.jpg');
const logo = require('../../../assets/logo.png');

const GOLD = '#C9A843';
const CARD_BG = 'rgba(13, 27, 42, 0.84)';
const CARD_BORDER = 'rgba(201, 168, 67, 0.22)';
const TEXT_WHITE = '#FFFFFF';
const TEXT_MUTED = 'rgba(255,255,255,0.58)';

type FieldErrors = { email?: string; password?: string; confirm?: string };

export default function RegisterScreen() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const { signUp } = useAuth();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const [accepted, setAccepted] = useState(false);

  const params = useLocalSearchParams<{ email?: string; agreement?: string }>();

  const [email, setEmail] = useState(params.email ?? '');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  // Honeypot — αόρατο πεδίο μόνο στο web (bots συνήθως το συμπληρώνουν αυτόματα,
  // πραγματικοί χρήστες ποτέ δεν το βλέπουν). Αν έχει τιμή, η φόρμα απορρίπτεται
  // σιωπηλά χωρίς να αποκαλύψουμε στο bot γιατί απέτυχε.
  const [website, setWebsite] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (params.agreement) void setPendingInviteAgreement(params.agreement);
  }, [params.agreement]);

  async function onSubmit() {
    setFormError(null);
    if (website.trim()) return; // honeypot triggered — silently no-op
    const next: FieldErrors = {};
    next.email = validateEmail(email) ?? undefined;
    next.password = validatePassword(password) ?? undefined;
    if (!next.password && confirm !== password) {
      next.confirm = t('errors.passwordMismatch');
    }
    if (next.email || next.password || next.confirm) {
      setErrors(next);
      return;
    }
    if (!accepted) {
      setFormError(t('errors.termsRequired'));
      return;
    }
    setErrors({});

    setLoading(true);
    const { error, needsConfirmation } = await signUp(email, password);
    setLoading(false);

    if (error) {
      setFormError(error);
      return;
    }

    if (needsConfirmation) {
      appAlert(
        t('auth.register.success_title'),
        t('auth.register.success_body'),
      );
    }
  }

  return (
    <ImageBackground source={wallpaper} style={styles.flex} resizeMode="cover">
      <View style={styles.overlay} />
      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <ScrollView
          contentContainerStyle={[
            styles.content,
            { paddingTop: insets.top + spacing.xl, paddingBottom: insets.bottom + spacing.lg },
          ]}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.logoWrap}>
            <Image source={logo} style={styles.logo} resizeMode="contain" />
          </View>

          <View style={styles.card}>
            <View style={styles.header}>
              <Text style={styles.brand}>{t('auth.register.title')}</Text>
              <Text style={styles.subtitle}>
                {t('auth.register.subtitle')}
              </Text>
            </View>

            <View style={styles.form}>
              {Platform.OS === 'web' && (
                <View style={styles.honeypot} accessibilityElementsHidden importantForAccessibility="no-hide-descendants">
                  <Input
                    label="Website"
                    value={website}
                    onChangeText={setWebsite}
                    autoComplete="off"
                  />
                </View>
              )}
              <Input
                label="Email"
                value={email}
                onChangeText={setEmail}
                error={errors.email}
                placeholder="name@example.com"
                keyboardType="email-address"
                autoCapitalize="none"
                autoComplete="email"
                textContentType="emailAddress"
              />
              <Input
                label={t('auth.field.password')}
                value={password}
                onChangeText={setPassword}
                error={errors.password}
                placeholder={t('placeholder.password')}
                secureTextEntry={!showPassword}
                autoCapitalize="none"
                autoComplete="password-new"
                textContentType="newPassword"
                rightElement={
                  <Pressable onPress={() => setShowPassword((v) => !v)} hitSlop={8}>
                    <Ionicons name={showPassword ? 'eye-off-outline' : 'eye-outline'} size={20} color={TEXT_MUTED} />
                  </Pressable>
                }
              />
              <Input
                label={t('auth.field.confirmPassword')}
                value={confirm}
                onChangeText={setConfirm}
                error={errors.confirm}
                placeholder={t('placeholder.passwordConfirm')}
                secureTextEntry={!showConfirm}
                autoCapitalize="none"
                rightElement={
                  <Pressable onPress={() => setShowConfirm((v) => !v)} hitSlop={8}>
                    <Ionicons name={showConfirm ? 'eye-off-outline' : 'eye-outline'} size={20} color={TEXT_MUTED} />
                  </Pressable>
                }
              />

              <Pressable
                style={styles.consentRow}
                onPress={() => setAccepted((v) => !v)}
                accessibilityRole="checkbox"
                accessibilityState={{ checked: accepted }}
              >
                <Ionicons
                  name={accepted ? 'checkbox' : 'square-outline'}
                  size={26}
                  color={accepted ? GOLD : 'rgba(255,255,255,0.85)'}
                />
                <Text style={styles.consentText}>
                  {t('auth.register.consent.accept')}{' '}
                  <Text
                    style={styles.consentLink}
                    onPress={() => router.push({ pathname: '/legal', params: { doc: 'terms' } })}
                  >
                    {t('auth.register.consent.terms')}
                  </Text>{' '}
                  {t('auth.register.consent.and')}{' '}
                  <Text
                    style={styles.consentLink}
                    onPress={() => router.push({ pathname: '/legal', params: { doc: 'privacy' } })}
                  >
                    {t('auth.register.consent.privacy')}
                  </Text>
                  .
                </Text>
              </Pressable>

              {!!formError && (
                formError.toLowerCase().includes('υπάρχει ήδη λογαριασμός') ? (
                  <View style={styles.duplicateEmailBox}>
                    <Text style={styles.duplicateEmailText}>
                      {t('auth.register.duplicate_email.body')}
                    </Text>
                    <Pressable
                      style={styles.duplicateEmailBtn}
                      onPress={() => router.replace({ pathname: '/(auth)/login', params: { email } })}
                    >
                      <Text style={styles.duplicateEmailBtnText}>{t('auth.register.duplicate_email.login')}</Text>
                    </Pressable>
                  </View>
                ) : (
                  <Text style={styles.formError}>{formError}</Text>
                )
              )}

              <Button title={t('auth.register.submit')} onPress={onSubmit} loading={loading} />
            </View>

            <View style={styles.footer}>
              <Text style={styles.footerText}>{t('auth.register.have_account')} </Text>
              <Link href="/(auth)/login" style={styles.footerLink}>
                {t('auth.register.login_link')}
              </Link>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </ImageBackground>
  );
}

function makeStyles(_colors: AppColors) {
  return StyleSheet.create({
    flex: { flex: 1 },
    overlay: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(13, 27, 42, 0.68)',
    },
    content: {
      // Σε μεγάλες οθόνες η φόρμα μένει κεντραρισμένη σε αναγνώσιμο πλάτος
      // αντί να τεντώνεται· σε κινητό το maxWidth δεν ενεργοποιείται ποτέ.
      width: '100%',
      maxWidth: contentMaxWidth.form,
      alignSelf: 'center',
      flexGrow: 1,
      paddingHorizontal: spacing.lg,
      gap: spacing.lg,
    },
    logoWrap: {
      alignItems: 'center',
      paddingTop: spacing.sm,
    },
    logo: {
      width: 160,
      height: 160,
    },
    card: {
      backgroundColor: CARD_BG,
      borderRadius: radius.xl,
      borderWidth: 1,
      borderColor: CARD_BORDER,
      padding: spacing.lg,
      gap: spacing.lg,
    },
    header: {
      gap: spacing.sm,
    },
    brand: {
      color: TEXT_WHITE,
      fontSize: fontSize.xl,
      fontWeight: '700',
    },
    subtitle: {
      color: TEXT_MUTED,
      fontSize: fontSize.md,
    },
    form: {
      gap: spacing.md,
    },
    honeypot: {
      position: 'absolute',
      opacity: 0,
      height: 1,
      width: 1,
      overflow: 'hidden',
      left: -9999,
    },
    consentRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: spacing.sm,
    },
    consentText: {
      flex: 1,
      color: TEXT_MUTED,
      fontSize: fontSize.sm,
      lineHeight: 20,
    },
    consentLink: {
      color: GOLD,
      fontWeight: '600',
    },
    formError: {
      color: '#F87171',
      fontSize: fontSize.sm,
    },
    duplicateEmailBox: {
      backgroundColor: 'rgba(248, 113, 113, 0.12)',
      borderWidth: 1,
      borderColor: 'rgba(248, 113, 113, 0.35)',
      borderRadius: radius.md,
      padding: spacing.md,
      gap: spacing.sm,
    },
    duplicateEmailText: {
      color: '#FECACA',
      fontSize: fontSize.sm,
      lineHeight: 20,
    },
    duplicateEmailBtn: {
      alignSelf: 'flex-start',
      backgroundColor: GOLD,
      borderRadius: radius.sm,
      paddingVertical: spacing.xs,
      paddingHorizontal: spacing.md,
    },
    duplicateEmailBtnText: {
      color: '#0D1B2A',
      fontSize: fontSize.sm,
      fontWeight: '700',
    },
    footer: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
    },
    footerText: {
      color: TEXT_MUTED,
      fontSize: fontSize.md,
    },
    footerLink: {
      color: GOLD,
      fontSize: fontSize.md,
      fontWeight: '600',
    },
  });
}
